import {
  Form,
  type UseHookFormProps,
  useHookForm,
} from "@/components/base/form/hook-form";
import { cx } from "@/utils/cx";
import { patchOpportunity } from "@fp/admin-api-client/client";
import type { OpportunityDto } from "@fp/admin-api-client/types/opportunityDto.js";
import { PayRateType } from "@fp/admin-api-client/types/payRateType.js";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpStatusCode } from "axios";
import { createContext, useContext, useMemo, useState } from "react";
import type { UseFormHandleSubmit, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { RateSection, rateSchemaFields, rateRefinement, rateRefinementMessage } from "./rate-section";

export const RateFormSchema = z
  .object({
    id: z.string(),
    ...rateSchemaFields,
  })
  .refine(
    rateRefinement,
    {
      message: rateRefinementMessage,
      path: ["numberOfUnits"],
    },
  );

export type RateFormSchema = z.infer<typeof RateFormSchema>;

export type RateFormContext = {
  form: UseFormReturn<RateFormSchema>;
  handleSubmit: ReturnType<UseFormHandleSubmit<RateFormSchema>>;
  transition: boolean;
  errorMessage: string | undefined;
};

export const RateFormContext = createContext<RateFormContext | undefined>(
  undefined,
);

export const DEFAULT_VALUES = {
  id: "",
  rateType: PayRateType.FlatRate,
  rate: "",
  numberOfUnits: String(1),
  useNumberOfUnits: false,
  useUnitsPerPerson: false,
  unitsPerPerson: String(1),
};

export const useRateForm = () => {
  const context = useContext(RateFormContext);
  if (!context) {
    throw new Error("RateFormContext not found");
  }
  return context;
};

export type RateFormProps = UseHookFormProps<RateFormSchema, OpportunityDto> & {
  defaultValues?: RateFormSchema;
  children: (context: RateFormContext) => React.ReactNode;
};

export const RateFormSubmitHandler = async (formValues: RateFormSchema) => {
  const result = await patchOpportunity(formValues.id, {
    payRate: {
      type: formValues.rateType,
      value: Number(formValues.rate),
    },
    numberOfUnits:
      formValues.useNumberOfUnits && formValues.numberOfUnits
        ? Number(formValues.numberOfUnits)
        : null,
    maxUnitsPerParticipant:
      formValues.useUnitsPerPerson && formValues.unitsPerPerson
        ? Number(formValues.unitsPerPerson)
        : null,
  } as Parameters<typeof patchOpportunity>[1]);

  if (result.status !== HttpStatusCode.Ok) {
    return {
      success: false as const,
      error: new Error("Unable to update opportunity"),
    };
  }

  return { success: true as const, data: result.data };
};

export const RateForm = ({
  defaultValues = DEFAULT_VALUES,
  onSubmit = RateFormSubmitHandler,
  children,
  ...props
}: RateFormProps) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const { form, handleSubmit, transition } = useHookForm<
    RateFormSchema,
    OpportunityDto
  >({
    resolver: zodResolver(RateFormSchema),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
    onSuccess: async (data) => {
      setErrorMessage(undefined);
      await props.onSuccess?.(data);
    },
    onError: async (errors) => {
      setErrorMessage("Unable to create job");
      await props.onError?.(errors);
    },
    onSubmit,
    ...props,
  });

  const context = useMemo(
    () => ({ form, handleSubmit, transition, errorMessage }),
    [form, handleSubmit, transition, errorMessage],
  );

  return (
    <RateFormContext.Provider value={context}>
      <Form {...form}>
        {typeof children === "function" ? children(context) : children}
      </Form>
    </RateFormContext.Provider>
  );
};

export const RateFormErrorMessage = () => {
  const { errorMessage } = useRateForm();

  if (!errorMessage) return null;

  return <p className="text-error-primary">{errorMessage}</p>;
};

export const RateFormContent = ({ className }: { className?: string }) => {
  const { form } = useRateForm();
  const rateType = form.watch("rateType");
  const useNumberOfUnits = form.watch("useNumberOfUnits") ?? false;
  const useUnitsPerPerson = form.watch("useUnitsPerPerson") ?? false;

  return (
    <div className={cx("flex flex-col gap-4", className)}>
      <RateSection
        control={form.control}
        fieldNames={{
          rateType: "rateType",
          rate: "rate",
          useNumberOfUnits: "useNumberOfUnits",
          numberOfUnits: "numberOfUnits",
          useUnitsPerPerson: "useUnitsPerPerson",
          unitsPerPerson: "unitsPerPerson",
        }}
        rateType={rateType}
        useNumberOfUnits={useNumberOfUnits}
        useUnitsPerPerson={useUnitsPerPerson}
      />
    </div>
  );
};
